<template>
  <div class="">
    <h4>Statistical Summary Editor</h4>
    <p class="text-muted">Configure statistical aggregations for weather data over time periods. Select weather elements and the statistical functions (max, min, mean, etc.) to calculate for each period.</p>

    <div class="row">
      <div class="col-md-5 border p-3">
        <h6 class="border-bottom">Period</h6>
        <select class="form-select" v-model="combinedPeriod" @change="updateFromCombinedPeriod">
          <optgroup label="Single Period">
            <option v-for="p in periods" :key="p" :value="p">{{ formatPeriodLabel(p) }}</option>
          </optgroup>
          <optgroup label="By Year">
            <option v-for="p in periodsWithYearBreakout" :key="p + '_BY_YEAR'" :value="p + '_BY_YEAR'">{{ formatPeriodLabel(p) }} by Year</option>
          </optgroup>
        </select>
      </div>

      <div class="col-md-7 border p-3">
        <h6 class="border-bottom">&nbsp;</h6>
        <h6>Fields</h6>

        <!-- Show message when no fields exist -->
        <div v-if="localDefn.fields.length === 0" class="alert alert-info d-flex align-items-center mb-3" role="alert">
          <i class="bi bi-info-circle me-2"></i>
          <div>
            <strong>No fields configured.</strong> Please add at least one field to define your statistical summary.
          </div>
        </div>

        <div
          class="card mb-2 position-relative"
          v-for="(field, index) in localDefn.fields"
          :key="index"
        >
          <div class="card-body p-2">
            <div class="row g-3 align-items-start">
              <div class="col-md-3">
                <select class="form-select" v-model="field.element" @change="onElementChange(field)">
                  <option disabled value="">-- select element --</option>
                  <option v-for="el in availableElements" :key="el" :value="el">{{ el }}</option>
                </select>
                <!--<small class="form-text text-muted ps-2">Element</small>-->
              </div>

              <div class="col-md-8">
                <div class="btn-group flex-wrap" role="group">
                  <button
                    v-for="agg in aggregates"
                    :key="agg"
                    type="button"
                    class="btn btn-sm me-1 mb-1 aggregate-btn"
                    :class="field.aggregates.includes(agg) ? 'btn-primary' : 'btn-outline-primary'"
                    @click="toggleAggregate(field, agg)"
                  >
                    {{ agg }}
                  </button>
                </div>
                <!--<small class="form-text text-muted d-block">Aggregates</small>-->
              </div>

              <div class="col-md-1 d-flex justify-content-end align-items-start">
                <button 
                  class="btn btn-sm btn-outline-primary mt-1" 
                  @click="removeField(index)" 
                  style="padding: 0.125rem 0.25rem;"
                  title="Remove field"
                >
                  <i class="bi bi-trash" style="font-size: 0.75rem;"></i>
                </button>
              </div>
            </div>

            <!-- Percentiles UI -->
            <div v-if="field.aggregates.includes('PERCENTILES')" class="mt-3">
              <label class="form-label">Percentiles (0–99)</label>
              <div class="d-flex flex-wrap gap-2 align-items-center">
                <div
                  v-for="(p, pIndex) in field.percentiles"
                  :key="pIndex"
                  class="input-group input-group-sm w-auto"
                >
                  <input
                    type="number"
                    class="form-control"
                    v-model.number="field.percentiles[pIndex]"
                    min="0"
                    max="99"
                  />
                  <button class="btn btn-outline-danger" type="button" @click="removePercentile(field, pIndex)">×</button>
                </div>
                <button class="btn btn-sm btn-outline-success" @click="addPercentile(field)">
                  <i class="bi bi-plus"></i> Add
                </button>
              </div>
            </div>
            <!--
            <div class="text-end mt-3">
              <button class="btn btn-sm btn-danger" @click="removeField(index)">Remove Field</button>
            </div>
            -->
            </div>
        </div>
        <button class="btn btn-outline-primary btn-sm" @click="addField">
          <i class="bi bi-plus"></i> Add Field
        </button>
      </div>
    </div>


    <!--<br />
    <button class="btn btn-primary" @click="emitDefinition" :disabled="!isValidDefn">✅ Apply</button>-->
  </div>
</template>

<script>
import { isValidStatisticalSummaryDefn } from './weatherstatisticsutils.js';
import {VcListUtils} from '/src/visualcrossing-core/vclistutils';
export default {
  name: 'StatisticalSummaryEditor',
  props: {
    sessionId:String,
    sessionState:Object,
    definition: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      periods: ['YEAR', 'MONTH', 'WEEK_OF_YEAR', 'DAY_OF_YEAR'], //, 'HOUR_OF_DAY' //hourly not supported in this editor
      aggregates: ['MAX', 'MIN', 'MEAN', 'SUM', 'PERCENTILES'],
      elements: {
        "tempmax": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "tempmin": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "temp": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "feelslikemax": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "feelslikemin": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "feelslike": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "dew": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "humidity": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "precip": { "aggregates": ["SUM"], type: "PER_PERIOD_SUM_STATS" },
        "precipprob": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "precipcover": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "snow": { "aggregates": ["SUM"] , type: "PER_PERIOD_SUM_STATS" },
        "snowdepth": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "windgust": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "windspeed": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "winddir": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "pressure": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "cloudcover": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "visibility": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "solarradiation": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "solarenergy": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "uvindex": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "severerisk": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "moonphase": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "windspeed50": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "winddir50": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "windspeed80": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "winddir80": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "windspeed100": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "winddir100": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "dniradiation": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "difradiation": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "ghiradiation": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "gtiradiation": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "sunelevation": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "soiltemp01": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "soiltemp04": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "soiltemp10": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "soiltemp20": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "soilmoisture01": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "soilmoisture04": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "soilmoisture10": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "soilmoisture20": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "soilmoisturevol01": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "soilmoisturevol04": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "soilmoisturevol10": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "soilmoisturevol20": { "aggregates": ["MAX", "MIN", "MEAN"] },
        "et0": { "aggregates": ["MAX", "MIN", "MEAN"] }
      },

      localDefn: {
        period: 'MONTH',
        breakOutByYear: false,
        fields: []
      }
    }
  },
  mounted() {
    if (this.definition && this.definition.fields) {
      this.localDefn = {
        period: this.definition.period || 'MONTH',
        breakOutByYear: this.definition.breakOutByYear || false,
        fields: this.definition.fields.map(f => ({
          element: f.element || '',
          type: f.type || 'PER_RECORD_STATS',
          aggregates: f.aggregates || [],
          percentiles: f.percentiles ? [...f.percentiles] : []
        }))
      }

    } else  {
        // If no fields defined, create default fields based on elements
      const elements = this.definition.elements ? VcListUtils.valuesInList(this.definition.elements) : ["tempmax", "tempmin"];

      this.localDefn.fields = elements
        .filter(el => this.elements[el]) // Skip elements that don't exist in this.elements
        .map(el => ({
          element: el,
          type: this.elements[el].type || 'PER_RECORD_STATS',
          aggregates: this.elements[el].aggregates || [],
          percentiles: []
        })); 
        

    } 
  },
  computed: {
    elementKeys() {
      return Object.keys(this.elements)
    },
    availableElements() {
      // Filter elements based on what's available in the definition
      if (this.definition && this.definition.elements) {
        const definitionElements = VcListUtils.valuesInList(this.definition.elements);
        return definitionElements.filter(el => this.elements[el]);
      }
      // If no definition elements specified, show all available elements
      return this.elementKeys;
    },
    isValidDefn() {
      const defn = this.localDefn;
      if (!defn || !isValidStatisticalSummaryDefn(defn)) {
        return null;
      }

      return defn;
    },
    periodsWithYearBreakout() {
      // Only periods that make sense to break out by year
      return this.periods.filter(p => p !== 'YEAR');
    },
    
    combinedPeriod() {
      // Create a combined value from period and breakOutByYear
      if (this.localDefn.breakOutByYear) {
        return this.localDefn.period + '_BY_YEAR';
      }
      return this.localDefn.period;
    }
  },
  methods: {
    addField() {
      this.localDefn.fields.push({
        element: '',
        type: 'PER_RECORD_STATS',
        aggregates: [],
        percentiles: []
      })
    },
    removeField(index) {
      this.localDefn.fields.splice(index, 1)
    },
    toggleAggregate(field, agg) {
      const idx = field.aggregates.indexOf(agg)
      if (idx >= 0) {
        field.aggregates.splice(idx, 1)
        if (agg === 'PERCENTILES') field.percentiles = []
      } else {
        field.aggregates.push(agg)
      }
    },
    addPercentile(field) {
      if (!field.percentiles) field.percentiles = []
      field.percentiles.push(50) // default value
    },
    removePercentile(field, index) {
      field.percentiles.splice(index, 1)
    },
    createDefinition() {
      return {
        period: this.localDefn.period,
        breakOutByYear: this.localDefn.breakOutByYear,
        fields: this.localDefn.fields.map(f => {
          const out = {
            element: f.element,
            type: f.type,
            aggregates: f.aggregates
          }
          if (f.aggregates.includes('PERCENTILES') && f.percentiles.length > 0) {
            const valid = f.percentiles.filter(p => !isNaN(p) && p >= 0 && p <= 99)
            if (valid.length) {
              out.percentiles = valid
            }
          }
          return out
        })
      }
    },  
    emitDefinition() {
      const defOut = this.createDefinition();

      /* {
        period: this.localDefn.period,
        breakOutByYear: this.localDefn.breakOutByYear,
        fields: this.localDefn.fields.map(f => {
          const out = {
            element: f.element,
            type: f.type,
            aggregates: f.aggregates
          }
          if (f.aggregates.includes('PERCENTILES') && f.percentiles.length > 0) {
            const valid = f.percentiles.filter(p => !isNaN(p) && p >= 0 && p <= 99)
            if (valid.length) {
              out.percentiles = valid
            }
          }
          return out
        })
      }*/
      this.$emit('update', defOut)
    },
    onElementChange(field) {
      if (!field.element) return;
      field.type = this.elements[field.element].type || 'PER_RECORD_STATS'
      field.aggregates = this.elements[field.element].aggregates
    },
    updateFromCombinedPeriod(event) {
      const value = event.target.value;
      if (value.endsWith('_BY_YEAR')) {
        // Extract the period and set breakOutByYear to true
        this.localDefn.period = value.replace('_BY_YEAR', '');
        this.localDefn.breakOutByYear = true;
      } else {
        // Just the period, no break by year
        this.localDefn.period = value;
        this.localDefn.breakOutByYear = false;
      }
    },
    formatPeriodLabel(period) {
      // Format period labels for better readability
      const labels = {
        'YEAR': 'Year',
        'MONTH': 'Month',
        'WEEK_OF_YEAR': 'Week of Year',
        'DAY_OF_YEAR': 'Day of Year'
      };
      return labels[period] || period;
    },
  },
  watch: {
    localDefn: {
      handler(newDefn, oldDefn) {
        if (!newDefn || !isValidStatisticalSummaryDefn(newDefn)) {
          console.log('Invalid definition:', newDefn);
          //return null;
        } else {
          console.log('Definition changed and valid:', newDefn);
        }
        this.emitDefinition();
        return newDefn;
      },
      deep: true // Deeply watch the properties of defn
    },
  }
}
</script>

<style scoped>
.card {
  border-left: 4px solid #0d6efd;
}

.aggregate-btn.btn-outline-primary:hover {
   color: #fff;
   opacity:50%
}

.aggregate-btn.btn-primary:hover {
  opacity:50%
  /*background-color: #0b5ed7;
  border-color: #0a58ca;
  color: #fff;*/
}
</style>
